import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { Button, Callout, Field, Input, Spinner } from '@zentera/ui';
import { Logo } from '../components/brand';
import { CopyButton } from '../components/common';
import { api, ApiError } from '../lib/api';
import { meQueryKey, safeNext, useMe } from '../lib/auth';

function AuthLayout({ title, description, children, footer }: { title: string; description?: ReactNode; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-subtle">
      <div className="flex flex-1 flex-col items-center px-4 pt-[14vh] pb-10">
        <div className="w-full max-w-[360px]">
          <div className="mb-8">
            <Logo height={22} />
          </div>
          <h1 className="text-lg font-medium text-fg">{title}</h1>
          {description && <p className="mt-1 text-sm text-fg-3">{description}</p>}
          <div className="mt-6">{children}</div>
          {footer && <div className="mt-6 text-xs text-fg-3">{footer}</div>}
        </div>
      </div>
      <footer className="px-4 py-5 text-center text-2xs text-fg-4">
        Acesso restrito à equipe Zentera. Todas as ações são registradas.
      </footer>
    </div>
  );
}

function FormError({ error }: { error: unknown }) {
  if (!error) return null;
  const msg = error instanceof ApiError ? error.message : 'Não foi possível concluir. Tente novamente.';
  return (
    <p role="alert" className="rounded-md border border-[#f3c9c5] bg-danger-subtle px-3 py-2 text-sm text-danger">
      {msg}
    </p>
  );
}

export function LoginPage() {
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const login = useMutation({
    mutationFn: () => api.post<{ mfa: 'verify' | 'setup' | 'none' }>('/api/auth/login', { email, password }),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: meQueryKey });
      if (r.mfa === 'verify') void navigate(`/login/mfa?next=${encodeURIComponent(next)}`);
      else if (r.mfa === 'setup') void navigate('/setup/mfa');
      else void navigate(next);
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate();
  };

  return (
    <AuthLayout title="Entrar no Zentera Admin" description="Use sua conta da equipe Zentera.">
      <form onSubmit={submit} className="space-y-4" noValidate>
        <FormError error={login.error} />
        <Field label="E-mail">
          <Input type="email" autoComplete="username" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Senha">
          <Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <Button type="submit" variant="primary" className="w-full" loading={login.isPending} disabled={!email || !password}>
          Continuar
        </Button>
      </form>
    </AuthLayout>
  );
}

function CodeInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Input
      inputMode="numeric"
      autoComplete="one-time-code"
      autoFocus
      maxLength={6}
      placeholder="000000"
      className="h-10 text-center font-mono text-lg tracking-[0.4em]"
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
    />
  );
}

export function MfaVerifyPage() {
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const [code, setCode] = useState('');
  const verify = useMutation({
    mutationFn: () => api.post('/api/auth/mfa/verify', { code }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: meQueryKey });
      void navigate(next);
    },
    onError: () => setCode(''),
  });

  if (me.isError) return <Navigate to="/login" replace />;
  if (me.data && me.data.mfaState !== 'verify') return <Navigate to={next} replace />;

  return (
    <AuthLayout title="Verificação em duas etapas" description="Digite o código de 6 dígitos do seu aplicativo autenticador.">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          verify.mutate();
        }}
      >
        <FormError error={verify.error} />
        <Field label="Código">
          <CodeInput value={code} onChange={setCode} />
        </Field>
        <Button type="submit" variant="primary" className="w-full" loading={verify.isPending} disabled={code.length !== 6}>
          Verificar
        </Button>
      </form>
    </AuthLayout>
  );
}

export function MfaSetupPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const [code, setCode] = useState('');
  const setup = useMutation({ mutationFn: () => api.post<{ secret: string; uri: string; qrSvg: string }>('/api/auth/mfa/setup') });
  const enable = useMutation({
    mutationFn: () => api.post('/api/auth/mfa/enable', { code }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: meQueryKey });
      void navigate('/');
    },
    onError: () => setCode(''),
  });

  if (me.isError) return <Navigate to="/login" replace />;
  if (me.data && me.data.mfaState !== 'setup') return <Navigate to="/" replace />;

  return (
    <AuthLayout
      title="Ative a verificação em duas etapas"
      description="Obrigatória para proprietários e administradores. Use um aplicativo autenticador (Google Authenticator, 1Password, Authy ou similar)."
    >
      {!setup.data ? (
        <div className="space-y-4">
          <FormError error={setup.error} />
          <Button variant="primary" className="w-full" loading={setup.isPending} onClick={() => setup.mutate()}>
            Gerar chave de configuração
          </Button>
        </div>
      ) : (
        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            enable.mutate();
          }}
        >
          <ol className="space-y-4 text-sm text-fg-2">
            <li>
              <p><span className="font-medium text-fg">1.</span> Escaneie o código no aplicativo autenticador.</p>
              <div
                className="mt-3 w-40 rounded-md border border-line bg-white p-3 [&_svg]:h-auto [&_svg]:w-full"
                // SVG gerado pelo próprio backend a partir do URI TOTP.
                dangerouslySetInnerHTML={{ __html: setup.data.qrSvg }}
                aria-label="QR code para o aplicativo autenticador"
                role="img"
              />
              <div className="mt-3 text-xs text-fg-3">Ou digite a chave manualmente:</div>
              <div className="mt-1 flex items-center gap-1 rounded-md border border-line bg-bg py-1 pr-1 pl-2.5">
                <code className="flex-1 font-mono text-xs break-all text-fg">{setup.data.secret.match(/.{1,4}/g)?.join(' ')}</code>
                <CopyButton value={setup.data.secret} label="Copiar chave" />
              </div>
            </li>
            <li>
              <p><span className="font-medium text-fg">2.</span> Digite o código exibido para confirmar.</p>
            </li>
          </ol>
          <FormError error={enable.error} />
          <Field label="Código">
            <CodeInput value={code} onChange={setCode} />
          </Field>
          <Button type="submit" variant="primary" className="w-full" loading={enable.isPending} disabled={code.length !== 6}>
            Ativar e continuar
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function PasswordChangePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const change = useMutation({
    mutationFn: () => api.post('/api/auth/password', { currentPassword: current, newPassword: next }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: meQueryKey });
      void navigate('/');
    },
  });
  const fieldErrors = change.error instanceof ApiError ? change.error.fieldErrors() : {};
  const mismatch = confirm.length > 0 && confirm !== next;

  if (me.isPending) return <div className="flex h-screen items-center justify-center"><Spinner /></div>;
  if (me.isError) return <Navigate to="/login" replace />;

  return (
    <AuthLayout title="Defina uma nova senha" description="Sua senha atual é temporária. Escolha uma senha com pelo menos 12 caracteres, com letras e números.">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!mismatch) change.mutate();
        }}
      >
        {!fieldErrors.newPassword && <FormError error={change.error} />}
        <Field label="Senha atual">
          <Input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
        </Field>
        <Field label="Nova senha" error={fieldErrors.newPassword} hint="Mínimo de 12 caracteres.">
          <Input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
        </Field>
        <Field label="Confirme a nova senha" error={mismatch ? 'As senhas não coincidem' : undefined}>
          <Input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Field>
        <Button type="submit" variant="primary" className="w-full" loading={change.isPending} disabled={!current || next.length < 12 || mismatch}>
          Salvar senha
        </Button>
      </form>
      {me.data?.mfaState === 'setup' && (
        <Callout className="mt-4" tone="neutral">Em seguida, você ativará a verificação em duas etapas.</Callout>
      )}
    </AuthLayout>
  );
}
